// <geo-input> — geometry editor with progressive enhancement.
//
// Without JS (or when initialisation fails) the element renders the
// `<.geo-input-fallback>` wrapper its host page provided — typically a
// bootstrap-styled <input type="text"> with a label, where the user can
// enter raw WKT/EWKT directly.
//
// When the closest <map-box> calls connectToMap(map), the element switches
// the fallback <input> to type="hidden" (so it still submits), shows a
// drawing toolbar, and registers OL Draw + Modify interactions on the map.
//
// The fallback `<.geo-input-fallback>` wrapper gets `display: none` via CSS
// keyed off `<geo-input data-enhanced>`.
//
// Recommended host markup:
//
//   <geo-input types="Point Polygon">
//     <div class="geo-input-fallback">
//       <%= form.text_field :geometry, value: @plot[:geometry]&.as_text %>
//     </div>
//   </geo-input>
//
// Attributes:
//   types  — space-separated list of allowed shape types (subset of
//            "Point", "LineString", "Polygon"). Default: "Point Polygon".
//   name   — fallback for the form-field name when no inner <input> is
//            found. Optional if a fallback input is present.
//   value  — fallback for the initial WKT/EWKT value. Optional if a
//            fallback input is present.
//
// Toolbar UX:
//   - Type buttons (Punkt/Linie/Fläche) act as a radio group: only one is
//     active at a time. Disabled while a draw is in progress.
//   - "Rückgängig" removes the last vertex while drawing (OL's
//     Draw.removeLastPoint). Disabled outside drawing.
//   - "Zurücksetzen" restores the geometry to its initial value.
//   - "Leeren" empties the geometry entirely.
//   - "Vereinfachen" reduces vertex count via Douglas-Peucker (tolerance 5m).
//   - "Auf Geometrie zoomen" fits the map view to the current geometry.
//   - Esc cancels an in-progress draw.
//   - Alt+click on a vertex (while not drawing) deletes it (Modify).
//   - Snaps to existing vector layer features.
//   - Coordinate readout (current pointer lon/lat) shown above the map
//     while a draw is active.
//   - Keyboard shortcuts when no input is focused:
//       P → Punkt, L → Linie, F → Fläche
//       Backspace → Rückgängig, Esc → abbrechen
//
// Form value format: "SRID=4326;<WKT>" (EWKT). PostGIS adapter accepts
// this for geography(GEOMETRY, 4326) columns directly; HasGeometry's
// before_save derives the centroid into `coords` for non-Point shapes.

import VectorSource from 'ol/source/Vector.js';
import VectorLayer from 'ol/layer/Vector.js';
import Feature from 'ol/Feature.js';
import Draw from 'ol/interaction/Draw.js';
import Modify from 'ol/interaction/Modify.js';
import Snap from 'ol/interaction/Snap.js';
import {altKeyOnly} from 'ol/events/condition.js';
import MousePosition from 'ol/control/MousePosition.js';
import {createStringXY} from 'ol/coordinate.js';
import {Style, Fill, Stroke, Circle} from 'ol/style.js';
import WKT from 'ol/format/WKT.js';
import crelt from 'crelt';


const DEFAULT_TYPES = 'Point Polygon';
const wktFormat = new WKT();
const SIMPLIFY_TOLERANCE_M = 5;

const editStyle = new Style({
  fill: new Fill({color: 'rgba(34, 34, 240, 0.2)'}),
  stroke: new Stroke({color: '#101071', width: 2}),
  image: new Circle({
    radius: 7,
    fill: new Fill({color: '#ffcc33'}),
    stroke: new Stroke({color: '#101071', width: 1}),
  }),
});

const previewStyle = new Style({
  fill: new Fill({color: 'rgba(34, 34, 240, 0.08)'}),
  stroke: new Stroke({color: '#101071', width: 2, lineDash: [6, 6]}),
  image: new Circle({
    radius: 5,
    fill: new Fill({color: 'rgba(255, 204, 51, 0.6)'}),
    stroke: new Stroke({color: '#101071', width: 1, lineDash: [3, 3]}),
  }),
});

const KEY_TO_TYPE = {p: 'Point', l: 'LineString', f: 'Polygon'};


export class GeoInput extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (this.setupComplete) return;

    this.allowedTypes = (this.getAttribute('types') || DEFAULT_TYPES)
        .split(/\s+/).filter(Boolean);

    // Find the fallback wrapper / input rendered by the form helper.
    this.fallbackWrapper = this.querySelector(':scope .geo-input-fallback');
    this.fallbackInput   = this.fallbackWrapper?.querySelector('input');

    this.fieldName    = this.fallbackInput?.name  || this.getAttribute('name');
    this.initialValue = this.fallbackInput?.value || this.getAttribute('value') || '';

    this.source = new VectorSource();
    this.preloadFromValue(this.initialValue);

    this.setupComplete = true;
    this.fireConnectedEvent();
  }

  disconnectedCallback() {
    if (this.map) {
      if (this.draw)   this.map.removeInteraction(this.draw);
      if (this.modify) this.map.removeInteraction(this.modify);
      if (this.snaps)  this.snaps.forEach(s => this.map.removeInteraction(s));
      if (this.layer)  this.map.removeLayer(this.layer);
      if (this.mousePosition) this.map.removeControl(this.mousePosition);
    }
    if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
    this.map = undefined;
    this.draw = undefined;
    this.modify = undefined;
    this.snaps = undefined;
    this.layer = undefined;
  }

  fireConnectedEvent() {
    this.dispatchEvent(new CustomEvent('geo-input:connected', {
      bubbles: true,
      cancelable: false,
    }));
  }

  // Called by MapBox once OSM consent has been granted and the map is ready.
  // Switches the element from fallback (visible text input) into enhanced
  // mode (hidden input + on-map drawing toolbar).
  connectToMap(map) {
    this.map = map;

    this.layer = new VectorLayer({
      source: this.source,
      style:  editStyle,
    });
    this.map.addLayer(this.layer);

    this.modify = new Modify({source: this.source, deleteCondition: altKeyOnly});
    this._onModifyEnd = () => this.serialize();
    this.modify.on('modifyend', this._onModifyEnd);
    this.map.addInteraction(this.modify);

    this.installSnaps();

    this._onKeydown = (e) => this.handleKeydown(e);
    document.addEventListener('keydown', this._onKeydown);

    this.installEnhancedUI();
  }

  // Add a Snap interaction for every existing vector source on the map,
  // and for any added later. Modify and Snap share self-snapping naturally.
  installSnaps() {
    this.snaps = [];
    const addSnapFor = (layer) => {
      const source = layer.getSource?.();
      if (source && source instanceof VectorSource && !this.snaps.some(s => s._geoInputSource === source)) {
        const snap = new Snap({source});
        snap._geoInputSource = source;
        this.map.addInteraction(snap);
        this.snaps.push(snap);
      }
    };
    this.map.getLayers().forEach(addSnapFor);
    this.map.getLayers().on('add', e => addSnapFor(e.element));
  }

  installEnhancedUI() {
    if (this.fallbackInput) {
      this.fallbackInput.type = 'hidden';
      this.hiddenInput = this.fallbackInput;
    } else {
      this.hiddenInput = crelt('input', {
        type:  'hidden',
        name:  this.fieldName,
        value: this.initialValue,
      });
      this.appendChild(this.hiddenInput);
    }

    this.toolbar = crelt('div', {
      class: 'geo-input-toolbar',
      role:  'toolbar',
      'aria-label': window.I18n.t('js.geo_input.aria.toolbar'),
    });

    this.typeGroup = crelt('div', {
      class: 'geo-input-types btn-group',
      role:  'radiogroup',
      'aria-label': window.I18n.t('js.geo_input.aria.type_group'),
    });
    this.typeButtons = {};
    this.allowedTypes.forEach(type => {
      const btn = crelt('button', {
        type:  'button',
        class: 'btn btn-outline-secondary btn-sm',
        role:  'radio',
        'aria-pressed': 'false',
        'data-geo-type': type,
        title: window.I18n.t(`js.geo_input.type_title.${type}`),
      }, window.I18n.t(`js.geo_input.type.${type}`));
      btn.addEventListener('click', e => {
        e.preventDefault();
        this.startDraw(type);
      });
      this.typeButtons[type] = btn;
      this.typeGroup.appendChild(btn);
    });
    this.toolbar.appendChild(this.typeGroup);

    this.actionGroup = crelt('div', {
      class: 'geo-input-actions btn-group',
      role:  'group',
      'aria-label': window.I18n.t('js.geo_input.aria.action_group'),
    });
    this.undoBtn = this.actionButton(
        window.I18n.t('js.geo_input.action.undo'),
        window.I18n.t('js.geo_input.action.undo_title'),
        () => this.undoLastPoint(),
        {disabled: true});
    this.simplifyBtn = this.actionButton(
        window.I18n.t('js.geo_input.action.simplify'),
        window.I18n.t('js.geo_input.action.simplify_title', {tolerance: SIMPLIFY_TOLERANCE_M}),
        () => this.simplifyGeometry());
    this.zoomBtn = this.actionButton(
        window.I18n.t('js.geo_input.action.zoom'),
        window.I18n.t('js.geo_input.action.zoom_title'),
        () => this.zoomToGeometry());
    this.resetBtn = this.actionButton(
        window.I18n.t('js.geo_input.action.reset'),
        window.I18n.t('js.geo_input.action.reset_title'),
        () => this.resetToInitial());
    this.clearBtn = this.actionButton(
        window.I18n.t('js.geo_input.action.clear'),
        window.I18n.t('js.geo_input.action.clear_title'),
        () => this.clearGeometry());
    this.actionGroup.appendChild(this.undoBtn);
    this.actionGroup.appendChild(this.simplifyBtn);
    this.actionGroup.appendChild(this.zoomBtn);
    this.actionGroup.appendChild(this.resetBtn);
    this.actionGroup.appendChild(this.clearBtn);
    this.toolbar.appendChild(this.actionGroup);

    this.pointCount = crelt('span', {class: 'geo-input-point-count badge', 'aria-live': 'polite'});
    this.toolbar.appendChild(this.pointCount);

    this.hint = crelt('p', {class: 'geo-input-hint help-block', 'aria-live': 'polite'});
    this.toolbar.appendChild(this.hint);

    this.appendChild(this.toolbar);

    this.activeType = this.geometryTypeOf(this.source);
    this.updateTypeButtons();
    this.updateActionButtons();
    this.updateHint();
    this.updatePointCount();

    this.dataset.enhanced = 'true';
  }

  actionButton(label, title, onClick, opts = {}) {
    const attrs = {
      type:  'button',
      class: 'btn btn-link btn-sm',
      title,
    };
    if (opts.disabled) attrs.disabled = 'disabled';
    const btn = crelt('button', attrs, label);
    btn.addEventListener('click', e => {
      e.preventDefault();
      onClick();
    });
    return btn;
  }

  geometryTypeOf(source) {
    const features = source.getFeatures();
    if (features.length === 0) return null;
    return features[0].getGeometry().getType();
  }

  // --- toolbar state -------------------------------------------------

  updateTypeButtons() {
    Object.entries(this.typeButtons).forEach(([type, btn]) => {
      const active = type === this.activeType;
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      btn.classList.toggle('active', active);
      btn.disabled = !!this.draw;
    });
  }

  updateActionButtons() {
    const empty = this.source.getFeatures().length === 0;
    this.undoBtn.disabled     = !this.draw;
    this.simplifyBtn.disabled = !!this.draw || empty || this.activeType === 'Point';
    this.zoomBtn.disabled     = empty;
    this.resetBtn.disabled    = !!this.draw;
    this.clearBtn.disabled    = !!this.draw && empty;
  }

  updateHint() {
    if (!this.hint) return;
    const key = this.draw ? `js.geo_input.hint.${this.activeType}` : 'js.geo_input.hint.idle';
    this.hint.textContent = window.I18n.t(key);
  }

  updatePointCount() {
    if (!this.pointCount) return;
    if (!this.draw || !this.activeFeature) {
      this.pointCount.textContent = '';
      this.pointCount.style.visibility = 'hidden';
      return;
    }
    const geom = this.activeFeature.getGeometry();
    let n = 0;
    switch (geom.getType()) {
      case 'Point':      n = 1; break;
      // OL appends the cursor as a sketch vertex while drawing.
      case 'LineString': n = Math.max(0, geom.getCoordinates().length - 1); break;
      case 'Polygon':    {
        const ring = geom.getCoordinates()[0] || [];
        // Polygon ring: [vertex_0, ..., vertex_n, cursor, vertex_0]
        n = Math.max(0, ring.length - 2);
        break;
      }
    }
    this.pointCount.textContent = window.I18n.t('js.geo_input.point_count', {count: n});
    this.pointCount.style.visibility = 'visible';
  }

  // --- drawing -------------------------------------------------------

  preloadFromValue(value) {
    if (!value) return;
    try {
      const wkt  = value.replace(/^SRID=\d+;/, '');
      const geom = wktFormat.readGeometry(wkt, {
        dataProjection:    'EPSG:4326',
        featureProjection: 'EPSG:3857',
      });
      this.source.addFeature(new Feature(geom));
    } catch (e) {
      console.warn(window.I18n.t('js.geo_input.missing_value_warning'), value, e);
    }
  }

  startDraw(type) {
    if (!this.map) return;
    if (this.draw) this.map.removeInteraction(this.draw);
    this.source.clear();

    this.activeType = type;

    this.draw = new Draw({source: this.source, type, style: previewStyle});
    this._onDrawStart = (event) => {
      this.activeFeature = event.feature;
      this._sketchListener = this.activeFeature.getGeometry().on('change', () => this.updatePointCount());
      this.updatePointCount();
    };
    this._onDrawEnd = () => {
      this.map.removeInteraction(this.draw);
      this.draw = undefined;
      this.activeFeature = undefined;
      this.updateTypeButtons();
      this.updateActionButtons();
      this.updateHint();
      this.updatePointCount();
      // drawend fires before the feature is added; defer serialise.
      setTimeout(() => this.serialize(), 0);
    };
    this.draw.on('drawstart', this._onDrawStart);
    this.draw.on('drawend',   this._onDrawEnd);
    this.map.addInteraction(this.draw);

    this.installMousePosition();
    this.updateTypeButtons();
    this.updateActionButtons();
    this.updateHint();
  }

  installMousePosition() {
    if (this.mousePosition || !this.map) return;
    this.mousePosition = new MousePosition({
      coordinateFormat: createStringXY(6),
      projection: 'EPSG:4326',
      className:  'ol-mouse-position',
      placeholder: '—',
    });
    this.map.addControl(this.mousePosition);
  }

  undoLastPoint() {
    if (this.draw) {
      this.draw.removeLastPoint();
      this.updatePointCount();
    }
  }

  cancelDraw() {
    if (!this.draw) return;
    this.map.removeInteraction(this.draw);
    this.draw = undefined;
    this.activeFeature = undefined;
    // Restore the previously-saved geometry from the hidden input value.
    this.source.clear();
    this.preloadFromValue(this.hiddenInput.value);
    this.activeType = this.geometryTypeOf(this.source);
    this.updateTypeButtons();
    this.updateActionButtons();
    this.updateHint();
    this.updatePointCount();
  }

  resetToInitial() {
    if (this.draw) {
      this.map.removeInteraction(this.draw);
      this.draw = undefined;
      this.activeFeature = undefined;
    }
    this.source.clear();
    this.preloadFromValue(this.initialValue);
    this.activeType = this.geometryTypeOf(this.source);
    this.updateTypeButtons();
    this.updateActionButtons();
    this.updateHint();
    this.updatePointCount();
    this.serialize();
  }

  clearGeometry() {
    if (this.draw) {
      this.map.removeInteraction(this.draw);
      this.draw = undefined;
      this.activeFeature = undefined;
    }
    this.source.clear();
    this.activeType = null;
    this.updateTypeButtons();
    this.updateActionButtons();
    this.updateHint();
    this.updatePointCount();
    this.serialize();
  }

  simplifyGeometry() {
    const features = this.source.getFeatures();
    if (features.length === 0) return;
    const geom = features[0].getGeometry();
    if (geom.getType() === 'Point') return;
    const simplified = geom.simplify(SIMPLIFY_TOLERANCE_M);
    features[0].setGeometry(simplified);
    this.serialize();
  }

  zoomToGeometry() {
    if (!this.map || !this.layer) return;
    const extent = this.layer.getSource().getExtent();
    if (!extent || extent.some(v => !isFinite(v))) return;
    this.map.getView().fit(extent, {padding: [40, 40, 40, 40], maxZoom: 17, duration: 250});
  }

  // --- keyboard ------------------------------------------------------

  handleKeydown(e) {
    if (e.key === 'Escape' && this.draw) {
      e.preventDefault();
      this.cancelDraw();
      return;
    }
    if (e.key === 'Backspace' && this.draw) {
      // Don't intercept if the user is typing into something.
      if (this.eventTargetIsInput(e.target)) return;
      e.preventDefault();
      this.undoLastPoint();
      return;
    }
    const type = KEY_TO_TYPE[e.key.toLowerCase()];
    if (type && this.allowedTypes.includes(type)) {
      if (this.eventTargetIsInput(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      e.preventDefault();
      this.startDraw(type);
    }
  }

  eventTargetIsInput(target) {
    return target instanceof HTMLInputElement
        || target instanceof HTMLTextAreaElement
        || target instanceof HTMLSelectElement
        || (target && target.isContentEditable);
  }

  // --- serialisation -------------------------------------------------

  serialize() {
    if (!this.hiddenInput) return;
    const features = this.source.getFeatures();
    if (features.length === 0) {
      this.hiddenInput.value = '';
    } else {
      const geom = features[0].getGeometry();
      const wkt = wktFormat.writeGeometry(geom, {
        dataProjection:    'EPSG:4326',
        featureProjection: 'EPSG:3857',
      });
      this.hiddenInput.value = `SRID=4326;${wkt}`;
    }
    this.updateActionButtons();
    this.dispatchEvent(new CustomEvent('geo-input:change', {
      bubbles: true,
      detail: {value: this.hiddenInput.value},
    }));
  }
}
